import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import http from '../config/http-service';

export interface ISupplierSearchData {
  id: number;
  name: string;
  email?: string;
  landLineNumber?: string;
  isActive?: boolean;
  countryCode?: string;
  imageUrl?: string;
  minimumOrder?: number;
  hasVerifiedEmail?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ISupplierSearchResponse {
  data: ISupplierSearchData[];
  total: number;
  page: number;
  limit: number;
}

const endpoint = 'suppliers';

type TData = ISupplierSearchResponse;
type TError = AxiosError;
type TQueryKey = [typeof endpoint, { filter: string }];

type Options = Omit<UseQueryOptions<TData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'> & {
  enabled?: boolean;
};
type ReturnType = UseQueryResult<TData, TError>;

/**
 * Search suppliers by name (minimum 2 characters).
 * Uses ?filter=name||$cont||demo for filtering.
 */
export function useSearchSuppliersApi(searchTerm: string, options?: Options): ReturnType {
  const isLoggedIn = useIsLoggedIn();
  const { enabled: userEnabled = true, ...restOptions } = options || {};

  // Use the filter param with $cont (contains) operator for name
  const filterValue = searchTerm.trim().length >= 2 ? `name||$cont||${searchTerm.trim()}` : '';

  const queryKey: TQueryKey = [endpoint, { filter: filterValue }];

  const queryFn = async (): Promise<TData> => {
    const params: Record<string, string> = {
      limit: '20',
      page: '1',
    };
    if (filterValue) {
      params['filter'] = filterValue;
    }

    const { data } = await http.get(endpoint, { params });
    return data;
  };

  return useQueryWrapper(queryKey, queryFn, {
    enabled: isLoggedIn && userEnabled && searchTerm.trim().length >= 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    ...restOptions,
  });
}
